// releaseMatching.js
//
// Pure release/version logic, kept free of Firebase and React so it can be unit-tested directly.
// releaseService.js re-exports the version helpers, so existing imports keep working.

/**
 * Semver-ish comparison, digits only (the versions this app uses). Returns >0 when a is newer.
 */
export const compareVersions = (a, b) => {
  const pa = String(a || '0.0.0').split('.').map((n) => parseInt(n, 10) || 0);
  const pb = String(b || '0.0.0').split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < 3; i += 1) {
    if ((pa[i] || 0) !== (pb[i] || 0)) return (pa[i] || 0) - (pb[i] || 0);
  }
  return 0;
};

/**
 * The highest version among everything we know about. Needed because the sources genuinely
 * disagree: the hand-written legacy notes stop at 4.3.0, while 4.3.1–4.3.4 shipped without notes,
 * so the newest ENTRY is not the newest RELEASE. Taking the newest entry alone offered "patch →
 * 4.3.1" for a build already running 4.3.4 — a version number that goes backwards over releases
 * that already exist.
 */
export const highestVersion = (...versions) => versions
  .filter(Boolean)
  .reduce((best, v) => (compareVersions(v, best) > 0 ? v : best), '0.0.0');

/**
 * Suggests the next version number from the last released one — patch by default, since most
 * releases are fixes. Purely a pre-fill; the Deploy tab lets it be overridden.
 */
export const suggestNextVersion = (lastVersion, bump = 'patch') => {
  const parts = String(lastVersion || '0.0.0').split('.').map((n) => parseInt(n, 10) || 0);
  const [major, minor, patch] = [parts[0] || 0, parts[1] || 0, parts[2] || 0];
  if (bump === 'major') return `${major + 1}.0.0`;
  if (bump === 'minor') return `${major}.${minor + 1}.0`;
  return `${major}.${minor}.${patch + 1}`;
};

/**
 * What the "Release as version" box should hold once the history changes.
 *
 * Real bug this replaces: the box was filled only while EMPTY, so after pushing 4.3.5 it still
 * read 4.3.5, and the next push failed with "Version 4.3.5 has already been released." A value the
 * person typed is kept only while it is still ahead of the last release; anything already used, or
 * behind, is replaced with the next patch.
 */
export const versionToOffer = (current, lastReleasedVersion) => {
  const typed = String(current || '').trim();
  if (typed && compareVersions(typed, lastReleasedVersion) > 0) return typed;
  return suggestNextVersion(lastReleasedVersion, 'patch');
};

const BUILD_HASH_PATTERN = /^main\.[a-f0-9]+$/;

const sameNotes = (a, b) => JSON.stringify(a || []) === JSON.stringify(b || []);

/**
 * The release that already shipped THIS build, or null if it has not gone out.
 *
 * `releases` must be newest first; the newest match wins (a build pushed twice reports its latest
 * release).
 *
 * Real bug this replaces: the Deploy tab called anything in PENDING_RELEASE "pending". Those notes
 * are compiled into the build, and a build can never change after it is released — the push
 * CLONES it — so the card could not go away, and it read as "still waiting" right after a
 * successful push. That is how 4.3.7 was released 93 seconds after 4.3.6 as the identical build.
 *
 * A build is identified by its main bundle's content hash (`main.7994735e`), which the push now
 * records. Releases recorded before that stored only the static APP_VERSION, which says nothing
 * about which build went out, so for those alone the notes are compared instead.
 */
export const findReleaseOfThisBuild = (releases, buildHash, pendingNotes) => (releases || []).find((release) => {
  if (buildHash && release.buildVersion === buildHash) return true;
  const hasBuildHash = BUILD_HASH_PATTERN.test(release.buildVersion || '');
  return !hasBuildHash && Array.isArray(pendingNotes) && pendingNotes.length > 0 && sameNotes(release.notes, pendingNotes);
}) || null;

/**
 * THE PENDING NOTES THAT HAVE NOT ACTUALLY BEEN RELEASED YET.
 *
 * Release notes are compiled into the bundle, and a push CLONES that bundle byte-for-byte, so a
 * push cannot empty the pending list. Emptying it is a separate step in the repo afterwards
 * (`node scripts/closeRelease.js <version>`, then rebuild). Skip that step and the list keeps
 * growing, and the NEXT push sends everything again.
 *
 * That is not hypothetical: 4.3.11 through 4.3.14 were each stamped with the identical 63 notes,
 * and on 2026-09-23 it happened again — 4.3.16 shipped 7 notes, then 4.3.17 shipped those same 7
 * plus its own 7. Warning about it was not enough, because the warning fires only above a
 * threshold and its advice is wrong whenever new notes have been written since the missed push.
 *
 * So the push no longer depends on anyone remembering. Whatever is already recorded against a real
 * release is filtered out here, and only the genuinely new notes are sent. Running closeRelease.js
 * stays the tidy thing to do — it keeps the repo honest and the list short — but forgetting it can
 * no longer re-announce anything to users.
 *
 * Compared on normalised text because the notes are prose that has been through a JS source file:
 * whitespace can differ, the words cannot.
 */
const normaliseNote = (note) => String(note || '').replace(/\s+/g, ' ').trim();

export const unreleasedNotes = (pendingNotes, releases) => {
  const already = new Set();
  (releases || []).forEach((release) => {
    (release?.notes || []).forEach((note) => already.add(normaliseNote(note)));
  });
  return (pendingNotes || []).filter((note) => !already.has(normaliseNote(note)));
};

/**
 * This build's identity, from the hashed main bundle CRA injects into index.html. The same string
 * on staging and production for the same build, because promotion clones files byte-for-byte.
 * Null in development (the dev server serves an unhashed bundle.js) and outside a browser.
 */
export const getBuildHash = () => {
  try {
    const script = document.querySelector('script[src*="/static/js/main."]');
    const match = script && script.getAttribute('src').match(/main\.([a-f0-9]+)\.js/);
    return match ? `main.${match[1]}` : null;
  } catch (error) {
    return null;
  }
};
